import { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import LoadingPage from "../../LoadingPage";
import { extractWebsiteSitemaps } from "../../../utils/api";
import CloseIcon from "../../../svg/CloseIcon";

interface SitemapProps {
  webLink?: string;                  // the link passed from WebLinksForm
  values: any;                       // form values (Formik)
  setFieldValue: (field: string, value: any) => void;
  open: boolean;
  onClose: () => void;
  upgradeTier: () => void;
  isLocked?: boolean;
}

const SitemapOptionsModal: React.FC<SitemapProps> = ({
  webLink,
  values,
  setFieldValue,
  open,
  onClose,
  upgradeTier,
  isLocked
}) => {
  const [formattedLink, setFormattedLink] = useState(webLink)
  const [loading, setLoading] = useState(true);
  const [sitemapData, setSitemapData] = useState<any>(null);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [timeout, setModalTimeout] = useState(null);
  const [showCheckbox, setShowCheckBox] = useState<boolean>(false)
  

  const theme = useTheme()
//   // toggle checkbox selection
//   const toggleSelection = (link: string) => {
//         setSelected((prev) => ({
//             ...prev,
//             [link]: !prev[link],
//         }));
//     };

  const handleHoverStart = () => {
        timeout && !showCheckbox && clearTimeout(timeout) 
        setModalTimeout(setTimeout(() => setShowCheckBox(true),50))
    };

    const handleHoverEnd = () => {
        timeout && clearTimeout(timeout);
        setShowCheckBox(false)
    };

  useEffect(() => {
    if (webLink) {
        const normalized = webLink.startsWith("http")
        ? webLink
        : "https://" + webLink;
        setFormattedLink(normalized);
    }
    }, [webLink]);

  // fetch sitemap when modal opens
  useEffect(() => {
    if (open && formattedLink) {
        setLoading(true);
        const tempUrl = formattedLink.replace(/(^\w+:|^)\/\//, ''); // Remove this once backend is complete with fixes
        extractWebsiteSitemaps(tempUrl).then((data) => {
        const init: Record<string, boolean> = {
            [formattedLink]: true, //  always normalized
        };
        data?.sitemap_list.forEach((s) => {
            init[s] = false;
        });
        setSitemapData(data);
        setSelected(init);
        setLoading(false);
        });
    } else {
        setSitemapData(null);
        setSelected({});
    }
    }, [open, formattedLink]);

  const handleAddWeblink = () => {
    if (!sitemapData) return;
    // only main link -> goes to userWebPage
    let copy = [...values.userWebPage];
    copy.push(
    {
        web_link: webLink,
        disabled: false,
        sitemap_links: sitemapData?.sitemap_count,
    }
    );
    setFieldValue('userWebPage', copy);
    onClose();
  };
  
  const handleAddSitemap = () => {
      if (!sitemapData) return;

      let copy = [...values.scrape_webpage];
      // Ensure the URL has http/https prefix
      copy.push({
        web_link: formattedLink,
        updatedAt: new Date().toISOString(),
        sitemap_links: sitemapData?.sitemap_count
      });
      setFieldValue('scrape_webpage', copy);
      onClose();
  };

  const handleCloseModal = () => {
    setSelected({})
    setShowCheckBox(false)
    setFormattedLink('')
    onClose();
  }

  return (
    <Dialog open={open} onClose={handleCloseModal} 
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: loading ? 8: 4,
          overflow: "hidden",
        },
      }}
      slotProps={{
        backdrop: {
          style: {
            backdropFilter: 'blur(4px)',
          }
        }
      }}
    >
      <Box sx={{ p: loading ? 1: 3, maxWidth: "500px" }}>
        {loading ? (
                <LoadingPage />
            ) : 
            (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    <Box>
                        <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                        >
                            <Typography variant="h6" color="text.primary">{isLocked ? "Upgrade to add all pages?": "Add all pages?"}</Typography>
                            <IconButton
                                onClick={handleCloseModal}
                            >
                                <CloseIcon stroke={theme.palette.text.primary} strokeWidth={2} />
                            </IconButton>
                        </Box>
                        {isLocked ? 
                        <Typography variant="body1" color="text.secondary">
                            This sitemap contains a sitemap with <strong>{sitemapData?.sitemap_count}</strong> webpages. Upgrade to <strong>Ultimate Tier</strong> to add all associated pages to your training material.
                        </Typography>
                        : 
                        <Typography variant="body1" color="text.secondary">
                                This URL contains a sitemap with <strong>{sitemapData?.sitemap_count}</strong> webpages. You can add first page only, or add all the pages.
                        </Typography>
                        }
                    </Box>

                    {/* <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            py:1,
                            px:2,
                            borderRadius: 2,
                            border: `1px solid ${theme.palette["_components"].input.standard.enabledBorder}`,
                            maxHeight: '33dvh',
                            overflowY: "auto",
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 2,
                                p:2,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                bgcolor: theme.palette.background["container-brand-1"]
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 2,
                                }}
                            >
                                <SitemapIcon fill={theme.palette.text.primary} stroke={theme.palette["nav"].iconactive} fillOpacity={0.04}/>
                                <Typography variant="h7">{formattedLink}</Typography>
                            </Box>
                            <Checkbox
                                size="small"
                                sx={{
                                    p: 0, // remove extra padding
                                    "& .MuiSvgIcon-root": {
                                    borderRadius: "50%", // circular look
                                    fontSize: 20,        // tighter size
                                    },
                                }}
                                icon={<RadioButtonUncheckedRounded />}
                                checkedIcon={<CheckCircleRounded sx={{color: theme.palette.action.active}}/>}
                                checked={!!selected[formattedLink] || showCheckbox}
                                onChange={() => toggleSelection(formattedLink)}
                            />
                        </Box> */}
                        {/* {sitemapData?.sitemap_list.map((sitemap, index) => (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 2,
                                    p:2,
                                    borderBottom: index !== (sitemapData?.sitemap_list.length-1) ? `1px solid ${theme.palette.divider}` : "",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        gap: 2,
                                    }}
                                >
                                    <WeblinkIcon stroke={theme.palette["nav"].iconactive}/>
                                    <Typography>{sitemap}</Typography>
                                </Box>
                                    <Checkbox
                                        size="small"
                                        sx={{
                                            p: 0, // remove extra padding
                                            "& .MuiSvgIcon-root": {
                                            borderRadius: "50%", // circular look
                                            fontSize: 20,        // tighter size
                                            },
                                        }}
                                        disabled={isLocked}
                                        icon={isLocked ? <DoNotDisturb /> :  <RadioButtonUncheckedRounded />}
                                        checkedIcon={<CheckCircleRounded sx={{color: theme.palette.action.active}}/>}
                                        checked={!!selected[sitemap] || showCheckbox}
                                        onChange={() => toggleSelection(sitemap)}
                                    />
                            </Box>
                        ))} */}
                        {/* <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                                alignItems: 'flex-start',
                                p: 2,
                                borderRadius: 2,
                                border: `1px solid ${theme.palette.divider}`,
                                bgcolor: theme.palette.background["container-brand-1"],
                            }}
                            >
                            <Box display="flex" alignItems="center" gap={1}>
                                <SitemapIcon fill={theme.palette.text.primary} stroke={theme.palette["nav"].iconactive}/>
                                <Typography variant="body2" color="text.primary">
                                <strong>{formattedLink}</strong>
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                This sitemap contains <strong>{sitemapData?.sitemap_count}</strong> webpages.
                            </Typography>
                        </Box> */}
                    {/* </Box> */}
                    {isLocked ? 
                        <Box sx={{
                            display: 'flex',
                            flexDirection: {xs: 'column-reverse', md:'row'},
                            justifyContent: {xs: 'start', md:'flex-end'},
                            gap: {xs:1, md:0},
                        }}>
                            {/* <Button onClick={handleCloseModal}>
                                <Typography sx={{color: theme.palette.secondary.contrastText}}>
                                    Cancel
                                </Typography>
                            </Button> */}

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: {xs:'column', md:'row'},
                                    gap: 1,
                                }}
                            >
                                <Button
                                    variant="contained"
                                    sx={{
                                        bgcolor: `${theme.palette.secondary.main} !important`,
                                        border: `1px solid ${theme.palette.secondary._states.outlinedBorder} !important`
                                    }}
                                    onClick={handleAddWeblink}
                                >
                                    <Typography sx={{color: theme.palette.secondary.contrastText}}>
                                        Add Main Link Only
                                    </Typography>
                                </Button>
                                <Button
                                    variant="contained"
                                    sx={{
                                        bgcolor: `${theme.palette.primary.main}`,
                                    }}
                                    onClick={upgradeTier}
                                >
                                    <Typography sx={{color: theme.palette.primary.contrastText}}>
                                        Upgrade to add all pages
                                    </Typography>
                                </Button>
                            </Box>
                        </Box>
                    :
                        <Box sx={{
                            display: 'flex',
                            flexDirection: {xs: 'column-reverse', md:'row'},
                            justifyContent: {xs: 'start', md:'flex-end'},
                            gap: {xs:1, md:1},
                        }}>
                            <Button
                                variant="contained"
                                sx={{
                                    bgcolor: `${theme.palette.secondary.main} !important`,
                                    border: `1px solid ${theme.palette.secondary._states.outlinedBorder} !important`
                                }}
                                onClick={handleAddWeblink}
                            >
                                <Typography sx={{color: theme.palette.secondary.contrastText}}>
                                    Add 1st page only
                                </Typography>
                            </Button>
                            <Button
                                variant="contained"
                                sx={{
                                    bgcolor: `${theme.palette.primary.main}`,
                                }}
                                onClick={handleAddSitemap}
                            >
                                <Typography sx={{color: theme.palette.primary.contrastText}}>
                                    Add All Webpages
                                </Typography>
                            </Button>
                        </Box>
                    }
                </Box>
            )
        }
      </Box>
    </Dialog>
  );
};

export default SitemapOptionsModal;